import { Box } from '@mui/material';
import { keyframes } from '@mui/material/styles';

type AIAgentOrbState = 'idle' | 'thinking' | 'building' | 'ready' | 'writing' | 'warning';

type AIAgentOrbProps = {
    state?: AIAgentOrbState;
    size?: number;
    palette?: Partial<AIAgentOrbPalette>;
};

export type AIAgentOrbPalette = {
    accent: string;
    core: string;
    wash: string;
    glow: string;
};

const orbDrift = keyframes`
  0% {
    transform: rotate(0deg) scale(1);
    background-position: 0% 50%;
  }
  45% {
    transform: rotate(9deg) scale(1.04);
    background-position: 100% 35%;
  }
  100% {
    transform: rotate(-5deg) scale(1);
    background-position: 0% 50%;
  }
`;

const orbPulse = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 0.64;
  }
  50% {
    transform: scale(1.08);
    opacity: 0.92;
  }
`;

const stateColor: Record<AIAgentOrbState, string> = {
    idle: '#7D8CFF',
    thinking: '#6F7DFF',
    building: '#2AABEE',
    ready: '#24A866',
    writing: '#0044FF',
    warning: '#E05A47',
};

export default function AIAgentOrb({ state = 'idle', size = 144, palette }: AIAgentOrbProps) {
    const accent = palette?.accent || stateColor[state];
    const core =
        palette?.core ||
        'radial-gradient(circle at 26% 30%, rgba(255,255,255,0.92) 0 10%, rgba(255,255,255,0.42) 26%, transparent 42%), linear-gradient(135deg, #DEE6FF 0%, #7A83FF 45%, #8FB7FF 100%)';
    const wash =
        palette?.wash ||
        `radial-gradient(circle at 42% 36%, rgba(255,255,255,0.86), transparent 28%), radial-gradient(circle at 72% 68%, ${accent}55, transparent 34%)`;
    const glow = palette?.glow || 'rgba(72, 96, 255, 0.14)';
    const isQuiet = state === 'idle' || state === 'ready';

    return (
        <Box
            aria-hidden="true"
            sx={{
                position: 'relative',
                width: size,
                height: size,
                borderRadius: '50%',
                flexShrink: 0,
                filter: 'saturate(1.08)',
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    inset: Math.max(8, Math.round(size * 0.08)),
                    borderRadius: '50%',
                    background: wash,
                    filter: 'blur(12px)',
                    opacity: 0.72,
                    animation: `${orbPulse} ${isQuiet ? '5.8s' : '2.8s'} ease-in-out infinite`,
                },
                '@media (prefers-reduced-motion: reduce)': {
                    '&::after, .ai-agent-orb-core': {
                        animation: 'none',
                    },
                },
            }}
        >
            <Box
                className="ai-agent-orb-core"
                sx={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '50%',
                    background: core,
                    backgroundSize: '160% 160%',
                    boxShadow: `inset 0 1px 0 rgba(255,255,255,0.68), inset 0 -${Math.round(
                        size * 0.12
                    )}px ${Math.round(size * 0.22)}px ${glow}`,
                    overflow: 'hidden',
                    animation: `${orbDrift} ${isQuiet ? '10s' : '4.6s'} ease-in-out infinite alternate`,
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        inset: -Math.round(size * 0.28),
                        background:
                            'linear-gradient(135deg, rgba(255,255,255,0.78) 0%, rgba(255,255,255,0.18) 22%, transparent 48%)',
                        filter: 'blur(8px)',
                        transform: 'rotate(-18deg)',
                    },
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        inset: Math.round(size * 0.1),
                        borderRadius: '50%',
                        background: `radial-gradient(circle at 76% 62%, ${accent}44, transparent 42%)`,
                        mixBlendMode: 'screen',
                    },
                }}
            />
        </Box>
    );
}
